import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

interface TopbarProps {
  onMenuClick: () => void;
  userName: string;
  userRole: string;
  pageLabel?: string;
}

export default function Topbar({ onMenuClick, userName, userRole, pageLabel }: TopbarProps) {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const roleLabelMap: Record<string, string> = {
    admin: "總管理員",
    manager: "主管",
    employee: "員工",
    trial: "試用用戶",
  };

  const handleLogout = async () => {
    setShowUserMenu(false);
    try {
      await supabase.auth.signOut();
    } catch {
      // ignore — sign out even if network error
    }
    navigate("/login");
  };

  return (
    <header className="h-16 bg-background-50 border-b border-background-200 flex items-center justify-between px-4 md:px-6 shrink-0">
      {/* Left: Mobile Menu + Breadcrumb */}
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-600 cursor-pointer"
        >
          <i className="ri-menu-line text-lg" />
        </button>
        <div className="hidden md:flex items-center gap-2 text-sm text-foreground-500">
          <span>酒藝商貿</span>
          <i className="ri-arrow-right-s-line text-xs" />
          <span className="text-foreground-700 font-medium">{pageLabel || "儀表板"}</span>
        </div>
      </div>

      {/* Right: User */}
      <div className="flex items-center gap-2 md:gap-4">
        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => {
              setShowUserMenu(!showUserMenu);
            }}
            className="flex items-center gap-2 md:gap-3 pl-2 pr-1 md:pr-2 py-1.5 rounded-lg hover:bg-background-100 cursor-pointer"
          >
            <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-background-50 text-sm font-semibold">
              {userName.charAt(0)}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium text-foreground-900 leading-tight">{userName}</p>
              <p className="text-xs text-foreground-500 leading-tight">{roleLabelMap[userRole] || userRole}</p>
            </div>
            <i className="ri-arrow-down-s-line text-foreground-400 text-sm hidden md:block" />
          </button>

          {/* User Dropdown */}
          {showUserMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowUserMenu(false)}
              />
              <div className="absolute right-0 top-full mt-2 w-56 bg-background-50 border border-background-200 rounded-xl shadow-lg z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-background-200">
                  <p className="text-sm font-semibold text-foreground-900">{userName}</p>
                  <p className="text-xs text-foreground-500">{roleLabelMap[userRole] || userRole}</p>
                </div>
                <div className="p-1.5">
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate("/profile");
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-700 hover:bg-background-100 text-left cursor-pointer"
                  >
                    <i className="ri-user-settings-line text-foreground-400" />
                    個人中心
                  </button>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate("/rbac");
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-700 hover:bg-background-100 text-left cursor-pointer"
                  >
                    <i className="ri-lock-2-line text-foreground-400" />
                    權限管理
                  </button>
                </div>
                <div className="p-1.5 border-t border-background-200">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-700 hover:bg-background-100 text-left cursor-pointer"
                  >
                    <i className="ri-logout-box-r-line text-foreground-400" />
                    登出
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}